/**
 * @fileoverview Section component for displaying the preview of the imported CSV file.
 * @module features/universal-transactions/components/upload/ImportPreviewSection
 */

import { useMemo, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Pencil, Plus } from 'lucide-react'
import PreviewTable from './PreviewTable'
import type { PreviewResponse, MappingDefinition } from '../../schemas/universal-transactions'
import universalTransactionsApi from '../../api/universalTransactions'
import CreateMappingPopover from '../mapping/CreateMappingPopover'
import EditMappingPopover from '../mapping/EditMappingPopover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'

interface SimpleMappingMeta {
  id: string
  name: string
  createdAt: string
}

interface ImportPreviewSectionProps {
  preview: PreviewResponse
  error: string | null
  mappings: SimpleMappingMeta[]
  selectedMappingId: string | null
  isIngesting: boolean
  onMappingSelect: (id: string) => void
  onImport: () => void
  onReset: () => void
  onMappingCreated?: (() => void) | undefined
}

export default function ImportPreviewSection({
  preview,
  error,
  mappings,
  selectedMappingId,
  isIngesting,
  onMappingSelect,
  onImport,
  onReset,
  onMappingCreated,
}: ImportPreviewSectionProps): ReactElement {
  const { t } = useTranslation('features-universal-transactions')

  const selectedMappingMeta = useMemo(
    () => mappings.find(m => m.id === selectedMappingId),
    [mappings, selectedMappingId]
  )

  // Load mapping details when selectedMappingId changes
  const { data: mappingDetail } = useQuery({
    queryKey: ['mapping', selectedMappingId],
    queryFn: () => universalTransactionsApi.getMapping(selectedMappingId!),
    enabled: !!selectedMappingId,
  })
  const selectedMapping = mappingDetail?.mapping ?? null

  const handleSaveMapping = async (
    mappingName: string,
    mappingDef: MappingDefinition,
    isUpdate: boolean,
    mappingId?: string
  ): Promise<void> => {
    // Coerce all column 'from' values to arrays
    const cols = mappingDef.columns as Record<string, unknown>
    const coerced: Record<string, unknown> = {}
    Object.keys(cols).forEach(key => {
      const current = cols[key]
      if (!current) {
        coerced[key] = current
        return
      }
      if (typeof current === 'object') {
        const fromVal = (current as { from?: unknown }).from
        const nextFrom = Array.isArray(fromVal)
          ? fromVal
          : typeof fromVal === 'string' && fromVal.length > 0
            ? [fromVal]
            : undefined
        coerced[key] = {
          ...(current as Record<string, unknown>),
          ...(nextFrom ? { from: nextFrom } : {}),
        }
      } else {
        coerced[key] = current
      }
    })
    const safeMapping: MappingDefinition = {
      ...mappingDef,
      columns: coerced as unknown as MappingDefinition['columns'],
    }
    const req = {
      name: mappingName,
      sampleHeader: preview.header,
      mapping: safeMapping,
    }

    try {
      if (isUpdate && mappingId) {
        await universalTransactionsApi.updateMapping(mappingId, req)
        toast.success(t('mapping.toast.updated', { defaultValue: 'Mapping "{{name}}" updated!', name: mappingName }))
      } else {
        const newMapping = await universalTransactionsApi.createMapping(req)
        toast.success(t('mapping.toast.created', { defaultValue: 'Mapping "{{name}}" created!', name: mappingName }))
        onMappingSelect(newMapping.id) // Select the newly created mapping
      }
      onMappingCreated?.()
    } catch (e: unknown) {
      console.error(`[ImportPreviewSection] ${isUpdate ? 'updateMapping' : 'createMapping'} failed`, e)
      toast.error(t('mapping.errors.saveFailed', { defaultValue: 'Failed to save mapping. Please try again.' }))
      throw e
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-medium">
          {t('preview.title', { defaultValue: 'Preview' })}
        </h2>
        <div className="text-xs text-muted-foreground">
          {t('preview.storedRows', { defaultValue: 'Stored rows' })}: {preview.totalStored}
        </div>
      </div>
      {error && (
        <div role="alert" className="text-sm text-destructive">
          <pre className="whitespace-pre-wrap font-sans">{error}</pre>
        </div>
      )}
      <PreviewTable header={preview.header} rows={preview.rows} mapping={selectedMapping || undefined} />
      <div className="pt-2 flex items-center justify-between">
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onReset}>
            {t('preview.button.chooseAnother', { defaultValue: 'Cancel' })}
          </Button>
        </div>
        <div className="flex items-center gap-2">

          
          <CreateMappingPopover
            header={preview.header}
            defaultName={t('mapping.editor.defaultName', { defaultValue: 'New Mapping' })}
            onSave={(name, def) => handleSaveMapping(name, def, false)}
            onCreated={onMappingCreated}
          >
            <Button variant="outline" size="sm" className="gap-1">
              {t('mapping.button.add', { defaultValue: 'Add' })}
              <Plus className="h-4 w-4" />
            </Button>
          </CreateMappingPopover>

          <Select value={selectedMappingId ?? ''} onValueChange={onMappingSelect}>
            <SelectTrigger id="mapping-select" className="w-56">
              <SelectValue placeholder={t('preview.selectPlaceholder', { defaultValue: 'Choose a mapping...' })} />
            </SelectTrigger>
            <SelectContent>
              {mappings.length === 0 ? (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  {t('mapping.selector.empty', { defaultValue: 'No saved mappings yet.' })}
                </div>
              ) : (
                mappings.map(m => (
                  <SelectItem key={m.id} value={m.id}>
                    <span className="flex items-center gap-2">
                      {m.name}
                      {preview.suggestedMappingId === m.id && (
                        <Badge variant="secondary" className="ml-1">
                          {t('preview.suggested', { defaultValue: 'Suggested' })}
                        </Badge>
                      )}
                    </span>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          {selectedMappingMeta && (
            <EditMappingPopover
              mapping={selectedMappingMeta}
              header={preview.header}
              onLoadMapping={async (id: string) => {
                const detail = await universalTransactionsApi.getMapping(id)
                return detail.mapping
              }}
              onSave={(name, def) => handleSaveMapping(name, def, true, selectedMappingMeta.id)}
              onDelete={async (mappingId: string) => {
                try {
                  await universalTransactionsApi.deleteMapping(mappingId)
                  onMappingSelect('') // Deselect after deletion
                  onMappingCreated?.()
                } catch (e: unknown) {
                  console.error('[ImportPreviewSection] deleteMapping failed', e)
                  throw e
                }
              }}
              onUpdated={onMappingCreated}
            >
              <Button variant="outline" size="sm">
                <Pencil className="h-4 w-4" />
              </Button>
            </EditMappingPopover>
          )}
          <Button
            type="button"
            size="sm"
            disabled={!selectedMappingId || isIngesting}
            onClick={onImport}
            className="ml-4"
          >
            {isIngesting
              ? t('mapping.button.importing', { defaultValue: 'Importing…' })
              : t('preview.button.import', { defaultValue: 'Import' })}
          </Button>
        </div>
      </div>
    </div>
  )
}
